import { useCallback, useEffect, useRef, useState } from "react";
import { getProperties } from "../api";

// Properties are searched/paginated server-side; we only ever load one page.
export const PAGE_SIZE = 20;
export const DEBOUNCE_MS = 350;

/**
 * Shared core for the server-side property search hooks.
 *
 * Owns the first-page fetch (limit 20), the loading flag, a stale-response
 * guard (only the latest request updates state) and a debounced search entry
 * point. Callers decide what to do with each response via `onResult`.
 *
 * @param {(data: object|undefined) => void} onResult - receives the response's
 *   `data` payload (i.e. `res.data.data`) on success, or `undefined` on error.
 * @returns {{
 *   loading: boolean,
 *   fetchProperties: (keyword: string) => void,
 *   debouncedFetch: (keyword: string) => void,
 * }}
 */
const useDebouncedPropertyFetch = (onResult) => {
  const [loading, setLoading] = useState(false);

  // Track the latest request so out-of-order responses are ignored.
  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const fetchProperties = useCallback((keyword) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);

    getProperties({ searchText: keyword, page: 1, limit: PAGE_SIZE })
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        onResultRef.current(res?.data?.data);
        setLoading(false);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        onResultRef.current(undefined);
        setLoading(false);
      });
  }, []);

  const debouncedFetch = useCallback(
    (keyword) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(
        () => fetchProperties((keyword || "").trim()),
        DEBOUNCE_MS
      );
    },
    [fetchProperties]
  );

  // Clear any pending debounce on unmount.
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  return { loading, fetchProperties, debouncedFetch };
};

export default useDebouncedPropertyFetch;
